/**
 * Utility Selector Widget
 *
 * A floating widget that reminds buyers to complete their utility
 * selection. Clicking the widget opens the utility selection flow
 * by emitting an event — it does not manage any parent container
 * directly.
 *
 * Events emitted:
 * - utility-widget:open-requested — User clicked widget; parent should open selection UI
 *
 * Events listened:
 * - utility-wizard:cleared — Hides widget
 * - utility-wizard:show-widget — Shows widget from container close
 * - MysaEvents.utilityPartnerChanged — Handles partner change events
 * - toggle (capture) — Hides widget on mobile when dropdown opens
 */

/**
 * @typedef {import("@theme/utility-partner-manager").UtilityPartner} UtilityPartner
 */

/**
 * @typedef {Object} WidgetRefs
 * @property {HTMLElement} logo - Provider logo container
 * @property {HTMLElement} name - Provider name display
 * @property {HTMLElement} status - Status text/badge
 */

import { Component } from "@theme/component";
import { UtilityStorage } from "@theme/utility-storage";
import { MysaEvents, hasUtilityQueryOverride } from "@theme/mysa-events";
import { createLogger } from "@theme/logger";

const log = createLogger("UtilitySelectorWidget");

/** @extends {Component<WidgetRefs>} */
class UtilitySelectorWidget extends Component {
  constructor() {
    super();

    /** @type {AbortController | null} */
    this.abortController = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.abortController = new AbortController();
    this.#bindEvents();

    if (this.dataset.showOnLoad === "true") {
      this.checkExistingSelection();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController?.abort();
  }

  #bindEvents() {
    if (!this.abortController) return;
    const { signal } = this.abortController;

    // Click to open selection UI
    this.addEventListener(
      "click",
      (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (!target.closest("[data-close]")) this.#requestOpen();
      },
      { signal },
    );

    // Keyboard support
    this.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.#requestOpen();
        }
      },
      { signal },
    );

    // Close button — dismiss for 1 day
    const closeBtn = this.querySelector("[data-close]");
    closeBtn?.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        this.#dismiss();
      },
      { signal },
    );

    // Wizard cleared — hide widget
    document.addEventListener("utility-wizard:cleared", () => this.#hide(), {
      signal,
    });

    // Container closed with incomplete state — show widget
    document.addEventListener(
      "utility-wizard:show-widget",
      (/** @type {CustomEvent} */ e) => this.#showFromContainerClose(e.detail),
      { signal },
    );

    // Utility partner changed from another component
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      (/** @type {CustomEvent} */ e) => this.#handleUtilityPartnerChanged(e),
      { signal },
    );

    // Mobile: hide widget when dropdown opens (toggle doesn't bubble, use capture)
    document.addEventListener(
      "toggle",
      (e) => {
        const target = /** @type {HTMLDetailsElement | null} */ (e.target);
        if (!target?.closest?.("postal-code-input")) return;
        if (target.open) this.#hide();
      },
      { capture: true, signal },
    );
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle utility partner changed - selection is complete, hide widget.
   * @param {CustomEvent<{partnerCode?: string}>} e
   */
  #handleUtilityPartnerChanged(e) {
    if (!e.detail?.partnerCode) return;
    this.#hide();
  }

  // ---------------------------------------------------------------------------
  // Selection & visibility
  // ---------------------------------------------------------------------------

  checkExistingSelection() {
    if (hasUtilityQueryOverride()) {
      log("Not showing: URL query param drives dropdown");
      return;
    }

    if (UtilityStorage.isDismissed?.(1)) {
      log("Not showing: dismissed in last 1 day");
      return;
    }

    const stored = UtilityStorage.getUtility?.();
    if (!stored?.code) return;

    log("Not showing: utility selection complete");
    this.#hide();
  }

  // ---------------------------------------------------------------------------
  // Display
  // ---------------------------------------------------------------------------

  /**
   * Show widget with provider info.
   * @param {UtilityPartner} [provider] - Provider data (undefined for generic state)
   * @param {Object} options
   * @param {boolean} [options.pending] - Show pending state
   * @param {boolean} [options.generic] - Show generic state without provider
   */
  #show(provider, { pending = false, generic = false } = {}) {
    const { logo, name, status } = this.refs;

    const statusEnrolled = this.dataset.statusEnrolled || "";
    const statusPending = this.dataset.statusPending || "";
    const statusSelection = this.dataset.statusSelection || "";
    const nameFallback = this.dataset.nameFallback || "";
    const nameGeneric = this.dataset.nameGeneric || "";

    if (logo) {
      if (!generic && provider?.logoImg) {
        const img = document.createElement("img");
        img.src = provider.logoImg;
        img.alt = provider.name || "";
        img.loading = "lazy";
        logo.innerHTML = "";
        logo.appendChild(img);
        logo.hidden = false;
      } else if (!generic && provider?.logoSvg) {
        logo.innerHTML = provider.logoSvg;
        logo.hidden = false;
      } else {
        logo.hidden = true;
      }
    }

    if (name) {
      if (generic) {
        name.textContent = nameGeneric;
      } else {
        name.textContent = provider?.name || nameFallback;
      }
    }

    // Status badge
    if (status) {
      if (generic) {
        status.textContent = statusSelection;
        status.classList.add("is-pending");
      } else if (pending) {
        status.textContent = statusPending;
        status.classList.add("is-pending");
      } else {
        status.textContent = statusEnrolled;
        status.classList.remove("is-pending");
      }
    }

    requestAnimationFrame(() => {
      this.classList.add("is-visible");
    });
  }

  #hide() {
    this.classList.remove("is-visible");
  }

  /**
   * Dismiss widget and set 1-day dismissal flag.
   */
  #dismiss() {
    this.#hide();
    UtilityStorage.setDismissed();
    log("Dismissed for 1 day");
  }

  /**
   * Show widget when container closes with incomplete state.
   * @param {{provider?: UtilityPartner, reason?: string}} detail
   */
  #showFromContainerClose(detail) {
    const reason = detail?.reason;

    log("showFromContainerClose:", { provider: detail?.provider, reason });

    if (!detail?.provider && reason === "utilities-shown") {
      log("Showing generic state");
      this.#show(undefined, { generic: true });
    } else {
      log("Not showing — selection complete or no match");
      this.#hide();
    }
  }

  /**
   * Emit event requesting the parent to open the selection UI.
   * The widget itself never manipulates external DOM.
   */
  #requestOpen() {
    this.#hide();
    UtilityStorage.clearDismissed?.();

    log("Emitting open-requested event");
    document.dispatchEvent(
      new CustomEvent("utility-widget:open-requested", {
        bubbles: true,
        detail: { source: "widget" },
      }),
    );
  }
}

if (!customElements.get("utility-selector-widget")) {
  customElements.define("utility-selector-widget", UtilitySelectorWidget);
}
